import React, {useState, useEffect} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import styles from './LoginEmailPage.module.css';
import logoSvg from './tables/static_files/kiraAi logo.svg';
import {ENDPOINT_BASE_URL} from './configs/config';

const EmailVerifyPage = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Get email from location state or sessionStorage
    const emailFromState = location.state?.email || sessionStorage.getItem('pendingEmail');
    if (!emailFromState) {
      // If no email, redirect back to login
      navigate('/email-login');
      return;
    }
    setEmail(emailFromState);
  }, [location, navigate]);

  const handleCodeChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split('').slice(0, 6);
      setCode(newCode);
      // Focus the last input
      const lastInput = document.getElementById('code-5');
      if (lastInput) lastInput.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const codeString = code.join('');
    
    if (codeString.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    if (!email || isSubmitting) return;

    setIsSubmitting(true);
    setError('');
    setStatus('Verifying code...');

    try {
      // Verify code
      const verifyResponse = await fetch(`${ENDPOINT_BASE_URL}/auth/verify-code`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          email,
          code: codeString
        })
      });

      let verifyData;
      try {
        verifyData = await verifyResponse.json();
      } catch (e) {
        throw new Error(verifyResponse.statusText || 'Failed to verify code');
      }

      if (!verifyResponse.ok || !verifyData.success) {
        throw new Error(verifyData.msg || verifyData.error || verifyData.message || 'Invalid code');
      }

      setStatus('Completing sign in...');

      // After verification, save and get token
      const saveResponse = await fetch(`${ENDPOINT_BASE_URL}/auth/save`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({
          email,
          type: '0' // '0' for email login
        })
      });

      let saveData;
      try {
        saveData = await saveResponse.json();
      } catch (e) {
        throw new Error(saveResponse.statusText || 'Failed to complete sign in');
      }

      // Check if response is ok (accepts 200-299 or 409)
      const isOk = (saveResponse.status >= 200 && saveResponse.status < 300) || saveResponse.status === 409;
      
      if (!isOk) {
        throw new Error(saveData.msg || saveData.error || saveData.message || 'Failed to complete sign in');
      }

      // Get hash_code from response (token)
      const hashCode = saveData?.data?.hash_code || saveData?.hash_code;
      
      if (!hashCode) {
        throw new Error('No token received from server');
      }

      // Store the token
      localStorage.setItem('gatewayToken', hashCode);
      sessionStorage.removeItem('pendingEmail');

      setStatus('Sign in successful! Redirecting...');
      // Redirect to swap page with token
      const isProduction = window.location.hostname === 'app.cutwaterglobal.com';
      if (isProduction) {
        window.location.href = `https://app.cutwaterglobal.com/swap?token=${encodeURIComponent(hashCode)}`;
      } else {
        navigate(`/swap?token=${encodeURIComponent(hashCode)}`, {replace: true});
      }
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
      setStatus('');
      // Clear code on error
      setCode(['', '', '', '', '', '']);
      const firstInput = document.getElementById('code-0');
      if (firstInput) firstInput.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (!email || isSubmitting) return;
    
    setIsSubmitting(true);
    setError('');
    setStatus('Resending code...');

      try {
        const response = await fetch(`${ENDPOINT_BASE_URL}/auth/send-verification-code`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({email})
        });

      let data;
      try {
        data = await response.json();
      } catch (e) {
        throw new Error(response.statusText || 'Failed to resend code');
      }

      if (!response.ok) {
        throw new Error(data.msg || data.error || data.message || 'Failed to resend code');
      }

      setStatus('Code resent! Please check your email.');
      setCode(['', '', '', '', '', '']);
      const firstInput = document.getElementById('code-0');
      if (firstInput) firstInput.focus();
      
      setTimeout(() => setStatus(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to resend code. Please try again.');
      setStatus('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    sessionStorage.removeItem('pendingEmail');
    navigate('/email-login');
  };

  if (!email) {
    return null; // Will redirect
  }

  return (
    <div className={styles.page}>
      <div className={styles.leftPane}>
        <div className={styles.leftInner}>
          <img className={styles.logo} src={logoSvg} alt="Cutwater" />
          <p className={styles.tagline}>
            Earn passive income automatically
            <br />
            with USDI stablecoin.
          </p>
        </div>
      </div>
      <div className={styles.rightPane}>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h1>Enter verification code</h1>
            <p>We sent a 6-digit code to {email}</p>
          </div>
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.codeInputContainer}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  id={`code-${index}`}
                  type="text"
                  inputMode="numeric"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  className={styles.codeInput}
                  disabled={isSubmitting}
                  autoFocus={index === 0}
                />
              ))}
            </div>
            {error && <span className={styles.error}>{error}</span>}
            {status && <span className={styles.status}>{status}</span>}
            <button 
              className={styles.primary} 
              type="submit" 
              disabled={code.join('').length !== 6 || isSubmitting}
            >
              Verify Code
            </button>
            <div className={styles.codeActions}>
              <button 
                type="button" 
                className={styles.link}
                onClick={handleResend}
                disabled={isSubmitting}
              >
                Resend code
              </button>
              <span className={styles.dividerText}>•</span>
              <button 
                type="button" 
                className={styles.link}
                onClick={handleBack}
                disabled={isSubmitting}
              >
                Change email
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EmailVerifyPage;

